/**
 * SE/portal anchor for static OG asset URLs. Consumer canonical and OG URLs are
 * per-market and resolve from CountryContext.config.host via SeoService.baseUrl;
 * this constant only backs the portal OG image (the B2B portal is single-market).
 */
export const SEO_BASE_URL = 'https://www.aposmart.se';

/** Separator between page title and brand name. */
export const SEO_TITLE_SEPARATOR = ' | ';

/** Brand suffix appended to every page title. */
export const SEO_TITLE_SUFFIX = 'Aposmart';

/**
 * Soft character limit for the full title (including suffix).
 *
 * Google displays ~580 px on desktop (~55-65 chars) and ~500 px on mobile
 * (~50-55 chars). There is no hard character limit; the full HTML title is
 * used for ranking regardless of display truncation (Ahrefs 953k-page study;
 * Google Search Central docs). 70 chars ensures the brand suffix is always
 * present in the HTML for ranking while keeping titles within Google's
 * typical rewrite threshold (99.9 % rewrite rate above 70 -- Ahrefs 2021).
 */
export const SEO_TITLE_MAX_LENGTH = 70;

/** Default OG image (absolute URL). */
export const SEO_DEFAULT_OG_IMAGE = `${SEO_BASE_URL}/assets/og-image.png`;

/** Portal OG image (absolute URL). Used for /portal, /b2b, /portal-landing and sub-routes. */
export const SEO_PORTAL_OG_IMAGE = `${SEO_BASE_URL}/assets/og-image-portal.png`;

/** Default OG image dimensions. */
export const SEO_DEFAULT_OG_IMAGE_WIDTH = '1200';
export const SEO_DEFAULT_OG_IMAGE_HEIGHT = '630';

/** Default OG image alt text. */
export const SEO_DEFAULT_OG_IMAGE_ALT = $localize`:@@seo.ogImage.alt:Aposmart - Jämför apotekspriser och spara pengar`;

/** Portal OG image alt text. */
export const SEO_PORTAL_OG_IMAGE_ALT = 'Aposmart Brand Portal - Marknadsanalys för consumer health';

/** Default meta description (fallback when none provided). */
export const SEO_DEFAULT_DESCRIPTION =
  $localize`:@@seo.defaultDescription:Jämför priser från alla apotek i Sverige. Hitta billigaste priset på medicin, hudvård, vitaminer och hälsoprodukter.`;

/** Default robots directive for indexable pages. */
export const SEO_DEFAULT_ROBOTS =
  'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';

/** Robots directive for pages that should not be indexed. */
export const SEO_NOINDEX_ROBOTS = 'noindex, follow';

/** ID prefix for dynamically injected JSON-LD script elements. */
export const SEO_JSONLD_ID_PREFIX = 'seo-jsonld-';

/** DOM ids for the site-level JSON-LD scripts, kept out of SEO_JSONLD_ID_PREFIX so per-page syncJsonLd never removes them. */
export const SEO_SITE_JSONLD_ORG_ID = 'seo-site-jsonld-organization';
export const SEO_SITE_JSONLD_WEBSITE_ID = 'seo-site-jsonld-website';
